import { useEffect, useRef, type RefObject } from "react";

const ENTER_MS = 250;
const REDUCED_MOTION_MS = 150;
const EASING = "cubic-bezier(0.2, 0, 0, 1)";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Animação de entrada (FLIP) quando `active` fica true. */
export function useFlipFromElement(
  fromRef: RefObject<HTMLElement | null>,
  toRef: RefObject<HTMLElement | null>,
  active: boolean
) {
  const animRef = useRef<Animation | null>(null);

  useEffect(() => {
    if (!active) {
      animRef.current?.cancel();
      return;
    }

    let cancelled = false;

    const run = () => {
      if (cancelled) return;
      const target = toRef.current;
      if (!target) return;

      animRef.current?.cancel();

      const from = fromRef.current;
      const fromRect = from?.getBoundingClientRect();
      const toRect = target.getBoundingClientRect();

      let dx = 0;
      let dy = 0;
      if (fromRect) {
        dx =
          fromRect.left + fromRect.width / 2 - (toRect.left + toRect.width / 2);
        dy =
          fromRect.top + fromRect.height / 2 - (toRect.top + toRect.height / 2);
      }

      if (prefersReducedMotion()) {
        animRef.current = target.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: REDUCED_MOTION_MS,
          fill: "forwards",
        });
      } else {
        animRef.current = target.animate(
          [
            {
              transform: `translate(${dx}px, ${dy}px) scale(0.9)`,
              opacity: 0.4,
            },
            { transform: "translate(0, 0) scale(1)", opacity: 1 },
          ],
          { duration: ENTER_MS, easing: EASING, fill: "forwards" }
        );
      }

      animRef.current.onfinish = () => {
        target.style.transform = "";
        target.style.opacity = "";
      };
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      animRef.current?.cancel();
    };
  }, [active, fromRef, toRef]);
}
